import { useState, useEffect, useCallback } from 'react';
import { competenciaService } from 'services/competenciaService';
import { CompetenciaResponse, CompetenciaPayload } from 'types/competencia.types';

export const useCompetencias = (cursoId: number | null) => {
  const [competencias, setCompetencias] = useState<CompetenciaResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [saving, setSaving]   = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    if (!cursoId) return;
    setLoading(true); setError(null);
    try {
      const data = await competenciaService.listByCurso(cursoId);
      setCompetencias([...data].sort((a, b) => a.orden - b.orden));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar competencias');
    } finally { setLoading(false); }
  }, [cursoId]);

  useEffect(() => { cargar(); }, [cargar]);

  const crear = async (payload: CompetenciaPayload) => {
    setSaving(true); setSaveError(null);
    try {
      const nueva = await competenciaService.crear(payload);
      setCompetencias(prev => [...prev, nueva].sort((a, b) => a.orden - b.orden));
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Error al crear competencia');
    } finally { setSaving(false); }
  };

  const actualizar = async (id: number, payload: CompetenciaPayload) => {
    setSaving(true); setSaveError(null);
    try {
      const updated = await competenciaService.actualizar(id, payload);
      setCompetencias(prev =>
        prev.map(c => c.idCompetencia === id ? updated : c).sort((a, b) => a.orden - b.orden)
      );
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Error al actualizar');
    } finally { setSaving(false); }
  };

  const eliminar = async (id: number) => {
    setSaving(true); setSaveError(null);
    try {
      await competenciaService.eliminar(id);
      // Reordenar: asignar orden 1..N a las competencias restantes
      const restantes = competencias
        .filter(c => c.idCompetencia !== id)
        .sort((a, b) => a.orden - b.orden);
      const renumeradas = restantes.map((c, i) => ({ ...c, orden: i + 1 }));
      setCompetencias(renumeradas);
      // Persistir en backend solo las que cambiaron de número
      const cambiadas = renumeradas.filter((c, i) => c.orden !== restantes[i].orden);
      if (cambiadas.length > 0) {
        await Promise.allSettled(
          cambiadas.map(c => competenciaService.actualizar(c.idCompetencia, {
            cursoId: c.cursoId,
            nombre: c.nombre,
            descripcion: c.descripcion ?? undefined,
            orden: c.orden,
          })),
        );
      }
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Error al eliminar');
    } finally { setSaving(false); }
  };

  return { competencias, loading, error, saving, saveError, crear, actualizar, eliminar, recargar: cargar };
};
